// Conferir o rótulo antes de deixar aplicar.
//
// Em 18/09 uma bomba foi ligada no quarto dela com o soro de outra paciente.
// Quem pegou foi o filho, porque tinha um assistente à mão para olhar a foto.
// Quem passa a maior parte dos plantões no quarto não tem isso — e o sistema
// tinha todos os dados sem fazer nada com eles.
//
// Esta tela é feita para uma pessoa cansada, em pé, no corredor, com a
// enfermeira esperando. Por isso:
//
// - Um botão só, grande, e a câmera abre direto.
// - O resultado ocupa a tela inteira. Não é um aviso no canto.
// - A primeira linha diz O QUE FAZER, não o que o sistema achou.
// - Nunca diz "pode aplicar". Diz que o rótulo confere com o que está
//   registrado aqui — quem libera é a equipe.
import { useCallback, useEffect, useRef, useState } from 'react';
import { formatarHora } from '@medicov1/shared';
import { chamar, ErroApi } from '../api';
import './conferir.css';

type Veredito = 'pare' | 'confira' | 'confere';

interface Item {
  campo: string;
  esperado: string;
  noRotulo: string | null;
  situacao: 'bate' | 'parece_bater' | 'nao_bate' | 'nao_deu_para_ler' | 'nao_esta_no_rotulo';
}

interface Rotulo {
  paciente: string | null; dataNascimento: string | null; leito: string | null;
  medicamento: string | null; dose: string | null; via: string | null;
  volume: string | null; dataHora: string | null; observacao: string | null;
}

/** O que a máquina leu num rótulo manuscrito, antes de alguém olhar. */
interface Confirmar {
  etapa: 'confirmar';
  rotulo: Rotulo;
  modelo: string;
  /** Campos que a leitura já acertou: não precisam de olho nem de digitação. */
  jaConfere: string[];
  porque: string;
}

interface Resultado {
  etapa: 'veredito';
  veredito: Veredito;
  titulo: string;
  oQueFazer: string;
  itens: Item[];
  perguntas: string[];
  rotulo: Rotulo;
}

/**
 * Os campos do rótulo, na ordem em que estão nele.
 *
 * O medicamento vem primeiro, e não por acaso: é o campo que decide, é onde a
 * máquina mais erra, e é o único que a pessoa sempre vê. Os outros só aparecem
 * quando a leitura NÃO bateu — quem tem muito o que digitar não digita: fecha a
 * tela e aplica sem conferir.
 */
const CAMPOS = [
  { chave: 'medicamento', rotulo: 'Medicamento', sempre: true },
  { chave: 'paciente', rotulo: 'Nome do paciente', sempre: false },
  { chave: 'dataNascimento', rotulo: 'Data de nascimento', sempre: false },
  { chave: 'leito', rotulo: 'Leito', sempre: false },
] as const;

const COMO_SE_CHAMA: Record<string, string> = {
  paciente: 'o nome', dataNascimento: 'a data de nascimento', leito: 'o leito',
};

const BASE_API = import.meta.env.VITE_API_URL ?? '/api';

export function ConferirMedicacao({ pacienteId, aoVoltar }: {
  pacienteId: string;
  aoVoltar: () => void;
}) {
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  // O que a máquina leu num rótulo manuscrito, esperando alguém confirmar.
  const [conferindo, setConferindo] = useState<Confirmar | null>(null);
  const [lido, setLido] = useState<Record<string, string>>({});
  const [registrado, setRegistrado] = useState<string | null>(null);
  const [entrouNaLista, setEntrouNaLista] = useState(false);
  const [erro, setErro] = useState('');
  const [disponivel, setDisponivel] = useState<boolean | null>(null);
  const campoFoto = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Perguntar antes de abrir a câmera: prometer uma conferência que não vai
    // acontecer é pior do que não oferecer nenhuma.
    chamar<{ disponivel: boolean }>('/conferencia/disponivel')
      .then((r) => setDisponivel(r.disponivel))
      .catch(() => setDisponivel(false));
  }, []);

  const conferir = useCallback(async (arquivo: File) => {
    setEnviando(true);
    setErro('');
    setResultado(null);
    try {
      const corpo = new FormData();
      corpo.append('pacienteId', pacienteId);
      corpo.append('arquivo', arquivo);
      const r = await fetch(`${BASE_API}/conferencia`, {
        method: 'POST', body: corpo, credentials: 'include',
      });
      const json = await r.json();
      if (!r.ok) throw new ErroApi(r.status, json?.error ?? 'falhou', json?.message ?? 'não consegui conferir a foto');
      const dado = json.data as Resultado | Confirmar;
      if (dado.etapa === 'confirmar') {
        setConferindo(dado);
        setLido(Object.fromEntries(CAMPOS.map((c) => [c.chave, dado.rotulo[c.chave] ?? ''])));
      } else {
        setResultado(dado);
      }
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui conferir a foto');
    } finally {
      setEnviando(false);
      if (campoFoto.current) campoFoto.current.value = '';
    }
  }, [pacienteId]);

  /** O texto que a PESSOA garantiu, agora sim, vira veredito. */
  const concluir = useCallback(async () => {
    if (!conferindo) return;
    setEnviando(true);
    setErro('');
    try {
      const r = await chamar<Resultado>('/conferencia/veredito', {
        metodo: 'POST',
        corpo: {
          pacienteId,
          paciente: lido.paciente ?? null,
          dataNascimento: lido.dataNascimento ?? null,
          leito: lido.leito ?? null,
          medicamento: lido.medicamento ?? null,
          dose: conferindo.rotulo.dose,
          modelo: conferindo.modelo,
        },
      });
      setResultado(r);
      setConferindo(null);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui concluir a conferência');
    } finally {
      setEnviando(false);
    }
  }, [conferindo, lido, pacienteId]);

  const recomecar = useCallback(() => {
    setResultado(null); setConferindo(null); setLido({}); setErro('');
    setRegistrado(null); setEntrouNaLista(false);
  }, []);

  /**
   * Grava o que entrou, e a que horas.
   *
   * Fica o nome que estava NO RÓTULO, não o que o sistema esperava: se subiu
   * outra coisa na bomba, é isso que precisa estar escrito depois.
   */
  const registrar = useCallback(async (incluirNaLista = false) => {
    if (!resultado?.rotulo.medicamento) return;
    setEnviando(true);
    setErro('');
    try {
      const r = await chamar<{ entrouNaLista: boolean }>('/conferencia/registrar', {
        metodo: 'POST',
        corpo: {
          pacienteId,
          nome: resultado.rotulo.medicamento,
          dose: resultado.rotulo.dose,
          via: resultado.rotulo.via,
          veredito: resultado.veredito,
          incluirNaLista,
          rotulo: {
            paciente: resultado.rotulo.paciente,
            dataNascimento: resultado.rotulo.dataNascimento,
            leito: resultado.rotulo.leito,
          },
          observacao: resultado.veredito === 'confere'
            ? null
            : `Registrado mesmo com veredito ${resultado.veredito}.`,
        },
      });
      // Hora de Brasília, não a do relógio do celular.
      setRegistrado(formatarHora(new Date()));
      setEntrouNaLista(r.entrouNaLista);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui registrar');
    } finally {
      setEnviando(false);
    }
  }, [resultado, pacienteId]);

  /**
   * O rótulo é dela, mas o remédio não está na ficha.
   *
   * É a situação de todo dia em que a equipe começa um tratamento e ninguém do
   * quarto teve tempo de cadastrar. Distinta de "rótulo de outra pessoa", que é
   * o caso grave — e por isso só vale quando os três identificadores bateram.
   */
  const naoConsta = Boolean(
    resultado
    && resultado.itens.some((i) => i.campo === 'Medicamento' && i.situacao === 'nao_bate')
    && resultado.itens.every((i) => i.campo === 'Medicamento' || i.situacao === 'bate'),
  );

  return (
    <main className={`conf conf--${resultado?.veredito ?? 'neutro'}`}>
      <header className="conf__topo">
        <button className="botao botao--voltar" onClick={aoVoltar}>Voltar</button>
        <h1>Conferir medicação</h1>
      </header>

      {!resultado && !conferindo && (
        <section className="conf__abertura">
          <p className="conf__explica">
            Antes de deixarem aplicar qualquer coisa, <b>fotografe o rótulo</b>.
            O sistema compara o nome, a data de nascimento, o leito e o
            medicamento com o que está registrado aqui.
          </p>

          {disponivel === false && (
            <p className="aviso-erro">
              A leitura por foto está indisponível agora. <b>Confira o rótulo com
              os próprios olhos</b>: nome, data de nascimento e leito precisam ser
              os dela. Se algum não for, não deixe aplicar e chame a enfermagem.
            </p>
          )}

          <input
            ref={campoFoto} type="file" accept="image/*" capture="environment"
            className="conf__arquivo"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void conferir(f);
            }}
          />
          <button
            className="conf__botao" disabled={enviando || disponivel === false}
            onClick={() => campoFoto.current?.click()}>
            {enviando ? 'conferindo…' : 'Fotografar o rótulo'}
          </button>

          <p className="conf__dica">
            Enquadre o rótulo inteiro, com luz. O que importa é aparecer o nome,
            a data de nascimento, o leito e o nome do medicamento.
          </p>
          {erro && <p className="aviso-erro">{erro}</p>}
        </section>
      )}

      {conferindo && (
        <section className="conf__conferir">
          <h2 className="conf__titulo">Foi isto que eu li?</h2>
          <p className="conf__fazer">{conferindo.porque}</p>

          {conferindo.jaConfere.length > 0 && (
            <p className="conf__jaconfere">
              Já conferi no rótulo {conferindo.jaConfere.map((c) => COMO_SE_CHAMA[c]).join(', ')}
              {conferindo.jaConfere.length === 1 ? ' e bate' : ' e batem'} com os dados dela.
              Você só precisa olhar o que está abaixo.
            </p>
          )}

          <ul className="conf__campos">
            {CAMPOS.filter((c) => c.sempre || !conferindo.jaConfere.includes(c.chave)).map((c) => (
              <li key={c.chave} className="conf__campo">
                <label htmlFor={`conf-${c.chave}`}>{c.rotulo}</label>
                <input
                  id={`conf-${c.chave}`} type="text" autoComplete="off"
                  value={lido[c.chave] ?? ''}
                  placeholder="não consegui ler — escreva ou deixe em branco"
                  onChange={(e) => setLido((v) => ({ ...v, [c.chave]: e.target.value }))}
                />
              </li>
            ))}
          </ul>

          <p className="conf__dica">
            Corrija o que estiver diferente do papel. O que você não conseguir
            ler, deixe em branco — em branco vale como <b>não confirmado</b>, e é
            melhor que um palpite.
          </p>

          <div className="conf__acoes">
            <button className="conf__botao" disabled={enviando} onClick={() => void concluir()}>
              {enviando ? 'conferindo…' : 'Confirmar e conferir'}
            </button>
            <button className="botao botao--fantasma" onClick={recomecar}>
              Tirar outra foto
            </button>
          </div>
          {erro && <p className="aviso-erro">{erro}</p>}
        </section>
      )}

      {resultado && (
        <section className="conf__resultado">
          <div className={`conf__selo conf__selo--${resultado.veredito}`}>
            {resultado.veredito === 'pare' ? 'PARE'
              : resultado.veredito === 'confira' ? 'CONFIRA'
                : 'CONFERE'}
          </div>

          <h2 className="conf__titulo">{resultado.titulo}</h2>
          <p className="conf__fazer">{resultado.oQueFazer}</p>

          <ul className="conf__itens">
            {resultado.itens.map((i) => (
              <li key={i.campo} className={`conf__item conf__item--${i.situacao}`}>
                <span className="conf__item__campo">{i.campo}</span>
                <span className="conf__item__valores">
                  <b>{i.noRotulo ?? (i.situacao === 'nao_esta_no_rotulo' ? 'não vem neste rótulo' : 'não deu para ler')}</b>
                  <small>aqui: {i.esperado}</small>
                </span>
                <span className="conf__item__marca">
                  {i.situacao === 'bate' ? 'confere'
                    : i.situacao === 'nao_bate' ? 'NÃO CONFERE'
                      : i.situacao === 'parece_bater' ? 'parece bater — leia o rótulo'
                        : i.situacao === 'nao_esta_no_rotulo' ? 'não vem no rótulo'
                          : 'ilegível'}
                </span>
              </li>
            ))}
          </ul>

          {resultado.perguntas.length > 0 && (
            <div className="conf__perguntas">
              <h3>Para perguntar à enfermagem</h3>
              <ol>{resultado.perguntas.map((p, i) => <li key={i}>{p}</li>)}</ol>
            </div>
          )}

          {(resultado.rotulo.dose || resultado.rotulo.dataHora || resultado.rotulo.observacao) && (
            <p className="conf__lido">
              {resultado.rotulo.dose && <>Dose no rótulo: {resultado.rotulo.dose}. </>}
              {resultado.rotulo.volume && <>Volume: {resultado.rotulo.volume}. </>}
              {resultado.rotulo.dataHora && <>Preparado em: {resultado.rotulo.dataHora}. </>}
              {resultado.rotulo.observacao && <><br />Observação da leitura: {resultado.rotulo.observacao}</>}
            </p>
          )}

          <p className="conf__rodape">
            Isto compara o rótulo com o que está registrado neste sistema. Não é
            conferência da equipe e não substitui a prescrição — se houver
            qualquer dúvida, pergunte antes de deixar aplicar.
          </p>

          {/* Registrar é o fecho do que a pessoa veio fazer: conferiu, aplicou,
              fica escrito. Só aparece quando há um nome para gravar, e o texto
              muda conforme o veredito — registrar algo que não confere é uma
              decisão diferente de registrar a rotina, e não pode parecer igual. */}
          {resultado.rotulo.medicamento && (
            registrado ? (
              <p className="conf__registrado">
                Registrado às {registrado}: <b>{resultado.rotulo.medicamento}</b>.
                {entrouNaLista
                  ? ' Também passou a constar na ficha dela como medicação em uso.'
                  : ' Fica no histórico dela.'}
              </p>
            ) : naoConsta ? (
              /* O remédio não está na ficha dela, mas o rótulo é dela: pode ser
                 um começo de tratamento que ninguém teve tempo de cadastrar —
                 ou pode ser engano. Quem sabe qual dos dois é a enfermagem, e
                 por isso o texto manda perguntar ANTES, e o botão diz o que vai
                 acontecer com todas as letras. Incluir é decisão de gente. */
              <div className="conf__novo">
                <h3>Este remédio não está na ficha dela</h3>
                <p>
                  O rótulo é dela — nome, data de nascimento e leito conferem —,
                  mas <b>{resultado.rotulo.medicamento}</b> não consta no que ela
                  vem tomando. <b>Pergunte à enfermagem se é um tratamento novo.</b>
                </p>
                <button className="conf__botao" disabled={enviando}
                  onClick={() => void registrar(true)}>
                  {enviando ? 'gravando…' : 'É novo: incluir na ficha e registrar'}
                </button>
                <button className="conf__botao conf__botao--ressalva" disabled={enviando}
                  onClick={() => void registrar(false)}>
                  Só registrar, sem incluir na ficha
                </button>
              </div>
            ) : (
              <button
                className={`conf__botao ${resultado.veredito === 'confere' ? '' : 'conf__botao--ressalva'}`}
                disabled={enviando} onClick={() => void registrar()}>
                {enviando ? 'registrando…'
                  : resultado.veredito === 'confere'
                    ? 'Registrar que foi aplicado agora'
                    : 'Registrar assim mesmo, com a ressalva'}
              </button>
            )
          )}

          <div className="conf__acoes">
            <button className="conf__botao conf__botao--menor" onClick={recomecar}>
              Conferir outro rótulo
            </button>
            <button className="botao botao--voltar" onClick={aoVoltar}>Voltar ao monitor</button>
          </div>
        </section>
      )}
    </main>
  );
}
